import { Box, Button, Grid, Skeleton, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { BentoBrand } from "schemas/dashboard";
import { routes } from "schemas/routes";

import { FairPaySurveyResponse } from "features/FairPaySurvey/schemas";
import { fetcher } from "utils/api";
import { trackEvent } from "utils/tracking";

import PitchBrand from "./PitchBrand";

const styles = {
  badge: {
    my: 2,
    p: 2,
  },
  chip: {
    py: 1,
    px: 2,
    mb: 1,
    fontWeight: 700,
    fontSize: "18px",
    color: "#151F16",
    borderRadius: "20px",
    border: `1px solid rgba(21, 31, 22, 0.2)`,
  },
  caption: {
    width: "80%",
    textAlign: "center",
    mt: 1,
    opacity: "60%",
  },
  skeleton: {
    borderRadius: "20px",
  },
  button: {
    px: 3,
    py: 2,
    textAlign: "center",
    textTransform: "none",
    borderRadius: 20,
    fontSize: 19,
    my: 2,
    display: "block",
    lineHeight: "1.4",
  },
};

export default function PitchNext({
  surveyResponse,
}: {
  surveyResponse: FairPaySurveyResponse;
}) {
  const [loading, setLoading] = useState<boolean>(true);
  const [recommendedBrands, setRecommendedBrands] = useState<BentoBrand[]>([]);

  const fetchBrands = async () => {
    setLoading(true);

    try {
      const { brands } = await fetcher(
        `/api/organization/fair-pay-survey-responses/${surveyResponse.surveyUuid}/brands`,
        "GET",
      );
      setRecommendedBrands(brands);
    } catch {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBrands();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyResponse.surveyUuid]);

  if (loading) {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Skeleton
            variant="rectangular"
            width="100%"
            height={250}
            sx={styles.skeleton}
          />
        </Grid>
        <Grid item xs={12}>
          <Skeleton
            variant="rectangular"
            width="100%"
            height={250}
            sx={styles.skeleton}
          />
        </Grid>
      </Grid>
    );
  }

  return recommendedBrands?.length > 0 ? (
    <Grid item xs={12} justifyContent="center" container sx={styles.badge}>
      <Typography component="div" sx={styles.chip}>
        Brands to Pitch Next
      </Typography>
      <Typography sx={styles.caption} paragraph>
        Based on your past collaborations we pulled a list of brands we think
        you should pitch to.
        <br />
        <br />
        Use Bento to pitch confidently!
      </Typography>
      {recommendedBrands.map((bentoBrand) => (
        <PitchBrand bentoBrand={bentoBrand} key={bentoBrand.id} />
      ))}
      <Box display="flex" sx={{ width: "100%" }} justifyContent="center">
        <Button
          href={`/${routes.signUp}?s=fairpay`}
          rel="noopener noreferrer"
          target="_blank"
          variant="contained"
          color="primary"
          sx={styles.button}
          onClick={() => {
            trackEvent("Fair Pay - Pitch to Brands Button Clicked", {
              "Survey UUID": surveyResponse.surveyUuid,
            });
          }}
        >
          Pitch to these brands on Bento
        </Button>
      </Box>
    </Grid>
  ) : (
    <></>
  );
}
